import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  BookOpen,
  FileText,
  GraduationCap,
  Award,
  TrendingUp,
  Target,
  BarChart3,
  Edit,
  AlertCircle,
  Star,
  X,
  Crown,
  Gem,
  Shield,
  Flame,
  Circle,
  Zap,
  ChevronDown,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DailyCompetitionTube from '../components/DailyCompetitionTube';
import HonorBoardCard from '../components/HonorBoardCard';
import CastleVisual from '../components/CastleVisual';
import CastleCelebration from '../components/CastleCelebration';
import CastleShatter from '../components/CastleShatter';
import BadgesSection from '../components/BadgesSection';
import '../components/Castle.css';

const CompletedItemsModal = ({ open, onClose, title, itemType }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      setLoading(true);
      api.get(`/completed-items/${itemType}`)
        .then((res) => setItems(res.data))
        .catch(() => toast.error('فشل تحميل البيانات'))
        .finally(() => setLoading(false));
    }
  }, [open, itemType]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid={`completed-modal-${itemType}`}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-accent">{title}</h2>
          <button onClick={onClose} data-testid="close-modal-btn">
            <X className="w-6 h-6 text-muted-foreground" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
          ) : items.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">لا توجد بيانات</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="completed-items-table">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-right p-3 font-semibold">العنوان</th>
                    <th className="text-right p-3 font-semibold">المؤلف</th>
                    <th className="text-right p-3 font-semibold">الصفحات</th>
                    <th className="text-right p-3 font-semibold">التاريخ</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={`${item.title || ''}-${item.date || ''}-${idx}`} className="border-b hover:bg-muted/10">
                      <td className="p-3">{item.title}</td>
                      <td className="p-3">{item.author}</td>
                      <td className="p-3">{item.pages}</td>
                      <td className="p-3">{item.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProfilePage = () => {
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [level, setLevel] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartPeriod, setChartPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [completedModal, setCompletedModal] = useState({ open: false, type: '', title: '' });
  const [challengeValues, setChallengeValues] = useState([20, 50, 100]);
  const [competition, setCompetition] = useState({ male_count: 0, female_count: 0 });
  const [promotionToast, setPromotionToast] = useState(null);
  const [shatterActive, setShatterActive] = useState(false);
  const [trailOpen, setTrailOpen] = useState(false);

  useEffect(() => {
    refreshProfile();
    loadData();
  }, [chartPeriod]);

  const loadData = async () => {
    try {
      const [levelRes, chartRes, challengeRes] = await Promise.all([
        api.get('/levels/current'),
        api.get(`/reports/chart?period=${chartPeriod}`),
        api.get('/dropdown-options/challenge').catch(() => ({ data: { values: [20, 50, 100] } })),
      ]);
      setLevel(levelRes.data);
      // --- Castle promotion detection: compare to last seen castle index ---
      try {
        const rn = user?.reader_number || 'anon';
        const key = `castle_seen_${rn}`;
        const seen = parseInt(localStorage.getItem(key) || '-1', 10);
        const cur = levelRes.data?.castle_index ?? 0;
        // Only celebrate when we have a prior snapshot to compare against
        // (i.e., the reader has visited the profile at least once before).
        if (seen >= 0 && cur > seen) {
          setShatterActive(true);
          setPromotionToast({
            levelName: levelRes.data?.current_level?.name,
            color: levelRes.data?.current_level?.color,
          });
        }
        localStorage.setItem(key, String(cur));
      } catch {}
      setChartData(chartRes.data);
      setChallengeValues(challengeRes.data?.values || [20, 50, 100]);
      try {
        const compRes = await api.get('/daily-competition');
        setCompetition(compRes.data);
      } catch (e) { console.error('daily-competition fetch failed:', e); }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const Counter = ({ value, label, icon: Icon, color = 'text-primary', onClick, spanMobile = false }) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
      let start = 0;
      const end = parseInt(value) || 0;
      if (end === 0) { setCount(0); return; }
      const duration = 1000;
      const increment = end / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }, [value]);

    return (
      <div
        className={`ds-card-mini ${onClick ? 'is-interactive' : ''} ${spanMobile ? 'col-span-2 md:col-span-1' : ''}`}
        onClick={onClick}
        data-testid={`counter-${label}`}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        <div className="ds-card-mini-row">
          <div className="ds-card-mini-text">
            <p className="ds-kicker">{label}</p>
            <p className={`ds-card-mini-value ${color}`}>
              {count.toLocaleString('ar')}
            </p>
            {onClick && (
              <p className="ds-card-mini-hint">اضغط للتفاصيل ↓</p>
            )}
          </div>
          <div className="ds-card-mini-icon">
            <Icon className={`w-7 h-7 ${color} ${onClick ? 'opacity-40 group-hover:opacity-70' : 'opacity-25'} transition-opacity`} />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-accent">جارٍ التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 md:gap-8 ds-stack" data-testid="profile-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary to-accent rounded-2xl p-8 text-white shadow-xl">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">مرحبا، {user?.name}</h1>
            <p className="text-white/80 text-lg">{user?.full_name}</p>
            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                <span>التحدي: {user?.challenge}</span>
              </div>
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                <span>المنجزات: {user?.total_achievements}</span>
              </div>
            </div>
          </div>
          <Button
            onClick={() => navigate('/profile/edit')}
            variant="outline"
            className="bg-white text-primary hover:bg-white/90 border-none"
            data-testid="edit-profile-btn"
          >
            <Edit className="w-5 h-5 ml-2" />
            تعديل الملف
          </Button>
        </div>
      </div>

      {/* Statistics Grid — 7 unified mini-cards */}
      <div className="ds-mini-grid grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 md:gap-4">
        <Counter
          value={user?.books_count || 0}
          label="عدد الكتب"
          icon={BookOpen}
          color="text-primary"
          onClick={() => setCompletedModal({ open: true, type: 'books', title: 'الكتب المنجزة' })}
        />
        <Counter
          value={user?.studies_count || 0}
          label="الدراسات العلمية"
          icon={GraduationCap}
          color="text-secondary"
          onClick={() => setCompletedModal({ open: true, type: 'studies', title: 'الدراسات العلمية المنجزة' })}
        />
        <Counter
          value={user?.articles_count || 0}
          label="المقالات"
          icon={FileText}
          color="text-accent"
          onClick={() => setCompletedModal({ open: true, type: 'articles', title: 'المقالات المنجزة' })}
        />
        <Counter value={user?.total_pages || 0} label="صفحات المنجَز" icon={TrendingUp} color="text-primary" />
        <Counter value={user?.total_pages_read || 0} label="مجموع الصفحات المقروءة" icon={Layers} color="text-emerald-600" />
        <Counter value={user?.missed_quota || 0} label="الورد الفائت" icon={AlertCircle} color="text-orange-500" />
        <Counter value={user?.points || 0} label="النقاط" icon={Star} color="text-yellow-500" spanMobile />
      </div>

      {/* Daily Competition Tube */}
      <DailyCompetitionTube maleCount={competition.male_count} femaleCount={competition.female_count} />

      {/* Reading Castle — unified dashboard card */}
      {level && (
        <div className="ds-card" data-testid="level-journey-card">
          {/* Decorative geometric background */}
          <div
            className="ds-card-glow"
            style={{
              backgroundImage: `radial-gradient(circle at 30% 20%, ${level.current_level?.color || '#C19A6B'} 0%, transparent 40%), radial-gradient(circle at 70% 80%, ${level.next_level?.color || '#C19A6B'} 0%, transparent 40%)`,
            }}
          />

          <div className="relative grid md:grid-cols-[auto_1fr] gap-6 md:gap-8 items-center">
            {/* LEFT: Castle hero */}
            <div className="flex flex-col items-center">
              <p className="ds-kicker mb-1">قلعتك</p>
              <h3 className="ds-title mb-3" style={{ color: level.current_level?.color }}>
                {level.current_level?.name}
              </h3>
              <CastleVisual
                index={level.castle_index ?? 0}
                fillPercent={level.display_percent ?? level.fill_percent ?? 0}
                cracked={!!level.cracked}
                color={level.current_level?.color}
                size="lg"
              />
            </div>

            {/* RIGHT: Progress + status + toggle */}
            <div className="flex flex-col gap-4 min-w-0">
              {/* Progress */}
              <div>
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-xs text-muted-foreground">
                    إتمام البناء
                    {level.today_boost_percent > 0 && (
                      <span className="mr-2 text-emerald-600 font-semibold" data-testid="today-boost-indicator">
                        + {level.today_boost_percent}% اليوم
                      </span>
                    )}
                  </span>
                  <span className="text-3xl font-extrabold leading-none" style={{ color: level.current_level?.color }}>
                    {level.display_percent ?? level.fill_percent ?? 0}%
                  </span>
                </div>
                <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden relative">
                  <div
                    className="h-full rounded-full transition-all duration-[1800ms] ease-out"
                    style={{
                      width: `${level.fill_percent ?? 0}%`,
                      background: `linear-gradient(90deg, ${level.current_level?.color}, ${level.current_level?.color}aa)`,
                    }}
                  />
                  {level.today_boost_percent > 0 && (
                    <div
                      className="absolute top-0 h-full rounded-full animate-pulse"
                      title={`إضافة اليوم: ${level.today_reports} تقرير، ${level.today_pages} صفحة`}
                      style={{
                        right: `${level.fill_percent ?? 0}%`,
                        width: `${level.today_boost_percent}%`,
                        background: 'repeating-linear-gradient(45deg, rgba(16,185,129,0.85) 0 6px, rgba(16,185,129,0.5) 6px 12px)',
                        boxShadow: '0 0 8px rgba(16,185,129,0.4)',
                      }}
                      data-testid="today-boost-bar"
                    />
                  )}
                </div>
                {level.next_level ? (
                  <p className="text-xs text-muted-foreground mt-2">
                    {level.streak_in_castle ?? 0} من {level.castle_size ?? 0} لبنة
                    <span className="mx-1.5 text-muted-foreground/50">•</span>
                    بعدها قلعة <span className="font-bold" style={{ color: level.next_level?.color }}>{level.next_level?.name}</span>
                  </p>
                ) : (
                  <div className="mt-2 flex items-center gap-2 text-sm font-bold" style={{ color: level.current_level?.color }}>
                    <span className="inline-block w-2 h-2 rounded-full" style={{ background: level.current_level?.color, boxShadow: `0 0 8px ${level.current_level?.color || '#8a5a2d'}` }} />
                    <span>بلغتَ قمة القلاع — رحلة مكتملة</span>
                  </div>
                )}
              </div>

              {/* Status note */}
              {level.cracked ? (
                <div className="flex items-start gap-2.5 bg-red-50/70 border border-red-200 rounded-xl p-3" data-testid="cracked-warning">
                  <span className="text-lg shrink-0">⚠️</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-red-700">قلعتك تتصدع</p>
                    <p className="text-[11px] text-red-600 mt-0.5 leading-snug">سجّل قراءة اليوم لإصلاح التصدع قبل أن تتنازل لقلعة أصغر.</p>
                  </div>
                </div>
              ) : level.fill_percent > 0 && (
                <p className="text-xs text-muted-foreground italic text-center md:text-right">
                  واصل البناء — كل تقرير لبنةٌ في قلعتك
                </p>
              )}

              {/* Expandable trail toggle */}
              <button
                type="button"
                onClick={() => setTrailOpen((o) => !o)}
                className="castle-trail-toggle self-center md:self-start"
                data-testid="toggle-level-trail"
                aria-expanded={trailOpen}
              >
                <span>{trailOpen ? 'إخفاء مسار القلاع' : 'اعرض مسار القلاع'}</span>
                <ChevronDown className={`w-4 h-4 castle-trail-chevron ${trailOpen ? 'is-open' : ''}`} />
              </button>
            </div>
          </div>

          {/* Collapsible levels trail — full-width, below the 2-column grid */}
          <div className={`castle-trail-wrapper relative ${trailOpen ? 'is-open' : ''}`} data-testid="level-trail-wrapper">
            <div className="castle-trail-inner">
              <div>
                {level.all_levels && level.all_levels.length > 0 && (() => {
                  const LEVEL_ICONS = [Circle, Shield, Zap, Crown, Gem, Flame];
                  const allLevels = level.all_levels;
                  const currentIdx = allLevels.reduce((acc, lv, i) => lv.achieved ? i : acc, 0);
                  return (
                    <>
                      {/* Desktop horizontal trail */}
                      <div className="hidden md:block">
                        <div className="relative flex items-start justify-between px-4">
                          <div className="absolute top-7 left-[8%] right-[8%] h-1 bg-gray-200 rounded-full z-0" />
                          {currentIdx > 0 && (
                            <div
                              className="absolute top-7 left-[8%] h-1 rounded-full z-[1] transition-all duration-1000"
                              style={{
                                width: `${(currentIdx / (allLevels.length - 1)) * 84}%`,
                                background: `linear-gradient(90deg, ${allLevels[0].color}, ${allLevels[currentIdx].color})`,
                              }}
                            />
                          )}
                          {allLevels.map((lv, i) => {
                            const Icon = LEVEL_ICONS[i] || Circle;
                            const isCurrent = i === currentIdx;
                            const isAchieved = lv.achieved;
                            return (
                              <div key={lv.level_id} className="relative z-10 flex flex-col items-center gap-2" style={{ width: `${100 / allLevels.length}%` }}>
                                <div
                                  className={`relative w-14 h-14 rounded-full flex items-center justify-center transition-all duration-500 ${
                                    isAchieved ? 'ring-2 ring-offset-2' : 'bg-gray-100 border-2 border-gray-200'
                                  } ${isCurrent ? 'scale-110' : ''}`}
                                  style={isAchieved ? {
                                    background: `linear-gradient(135deg, ${lv.color}, ${lv.color}dd)`,
                                    boxShadow: `0 0 20px ${lv.color}66, 0 0 40px ${lv.color}33`,
                                    ['--tw-ring-color']: lv.color,
                                  } : {}}
                                  data-testid={`level-badge-${lv.level_id}`}
                                >
                                  <Icon className={`w-6 h-6 ${isAchieved ? 'text-white drop-shadow-sm' : 'text-gray-300'}`} />
                                  {isCurrent && (
                                    <div className="absolute -inset-1 rounded-full animate-ping opacity-20" style={{ background: lv.color }} />
                                  )}
                                </div>
                                <span className={`text-xs font-bold text-center ${isAchieved ? 'text-accent' : 'text-gray-300'}`}>{lv.name}</span>
                                <span className={`text-[10px] ${isAchieved ? 'text-muted-foreground' : 'text-gray-300'}`}>{lv.min_streak} يوم</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      {/* Mobile vertical */}
                      <div className="md:hidden space-y-1">
                        {allLevels.map((lv, i) => {
                          const Icon = LEVEL_ICONS[i] || Circle;
                          const isCurrent = i === currentIdx;
                          const isAchieved = lv.achieved;
                          return (
                            <div key={lv.level_id} className="flex items-center gap-3">
                              <div className="flex flex-col items-center w-8">
                                {i > 0 && <div className={`w-0.5 h-3 ${allLevels[i - 1].achieved ? '' : 'bg-gray-200'}`} style={allLevels[i - 1].achieved ? { background: allLevels[i - 1].color } : {}} />}
                                <div
                                  className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isAchieved ? '' : 'bg-gray-100 border border-gray-200'} ${isCurrent ? 'ring-2 ring-offset-1' : ''}`}
                                  style={isAchieved ? {
                                    background: `linear-gradient(135deg, ${lv.color}, ${lv.color}dd)`,
                                    boxShadow: isCurrent ? `0 0 12px ${lv.color}66` : 'none',
                                    ['--tw-ring-color']: isCurrent ? lv.color : 'transparent',
                                  } : {}}
                                >
                                  <Icon className={`w-4 h-4 ${isAchieved ? 'text-white' : 'text-gray-300'}`} />
                                </div>
                                {i < allLevels.length - 1 && <div className={`w-0.5 h-3 ${isAchieved ? '' : 'bg-gray-200'}`} style={isAchieved ? { background: lv.color } : {}} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className={`text-sm font-bold ${isAchieved ? 'text-accent' : 'text-gray-300'}`}>{lv.name}</span>
                                <span className={`text-xs mr-2 ${isAchieved ? 'text-muted-foreground' : 'text-gray-300'}`}>({lv.min_streak} يوم)</span>
                              </div>
                              {isCurrent && <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-bold">حالي</span>}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reader Badges — extends the castle journey, never replaces it */}
      <BadgesSection
        atPeak={
          !!level &&
          level.all_levels &&
          level.all_levels.length > 0 &&
          (level.castle_index ?? 0) === level.all_levels.length - 1 &&
          (level.fill_percent ?? 0) >= 100
        }
      />

      {/* Honor Board — dynamic top streaks for males + females */}
      <HonorBoardCard />

      {/* Chart Section — unified dashboard card */}
      <div className="ds-card">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="ds-kicker">نشاطي القرائي</p>
            <h3 className="ds-title flex items-center gap-2.5">
              <BarChart3 className="w-6 h-6 text-primary" />
              الصفحات المقروءة
            </h3>
          </div>
          <div className="ds-pill-group" style={{ ['--pill-color']: '#C19A6B' }}>
            <button
              type="button"
              onClick={() => setChartPeriod('7d')}
              className={`ds-pill ${chartPeriod === '7d' ? 'is-active' : ''}`}
            >7 أيام</button>
            <button
              type="button"
              onClick={() => setChartPeriod('month')}
              className={`ds-pill ${chartPeriod === 'month' ? 'is-active' : ''}`}
            >شهر</button>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee5d6" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #C19A6B',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(40, 25, 10, 0.1)',
              }}
            />
            <Line
              type="monotone"
              dataKey="pages"
              stroke="#C19A6B"
              strokeWidth={2.5}
              dot={{ fill: '#C19A6B', r: 4 }}
              animationDuration={1200}
              animationEasing="ease-out"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* End of content */}

      {/* Completed Items Modal */}
      <CompletedItemsModal
        open={completedModal.open}
        onClose={() => setCompletedModal({ open: false, type: '', title: '' })}
        title={completedModal.title}
        itemType={completedModal.type}
      />

      {/* Castle promotion — full-screen shatter + congratulatory snippet */}
      {shatterActive && promotionToast && (
        <CastleShatter
          color={promotionToast.color}
          onDone={() => setShatterActive(false)}
        />
      )}
      {promotionToast && (
        <CastleCelebration
          levelName={promotionToast.levelName}
          color={promotionToast.color}
          onClose={() => setPromotionToast(null)}
        />
      )}
    </div>
  );
};

export default ProfilePage;
